/* eslint-disable no-unused-vars */
import debugFactory from "debug";
import { clientIdentifier, listenTo } from "@teaming/io-helpers";
import { SocketMessageType } from "@teaming/common-enums";

export function configureSockets( {
	storeName,
	store,
	ioSocket,
	ioOutcome,
} ) {
	const debug = debugFactory( `teaming:common-team:stores:${ storeName }:sockets` );

	const handleCreateUpdateTeamCommitment = async ( { body, clientIdentifier: originatingClientIdentifier, } ) => {
		if ( originatingClientIdentifier === clientIdentifier ) {
			return debug( "ignoring message from self" );
		}

		const { outcomeId, teamId: outcomeTeamId, } = body;
		const teamId = store.getters[ `${ storeName }/teamId` ];

		// message is not for this team, ignore
		if ( !teamId || outcomeTeamId !== teamId ) {
			return;
		}

		debug( `handling create/update commitment message for team ${ teamId }` );

		const promises = [
			ioOutcome.fetchCommitmentMetadata( { teamId, } ),
			ioOutcome.fetchOutcome( { outcomeId, } ),
		];

		const [
			commitmentMetadataResponse,
			outcomeResponse,
		] = await Promise.all( promises );

		store.commit( `${ storeName }/receiveMetadata`, { metadata: commitmentMetadataResponse.data, } );
		store.commit( `${ storeName }/receiveUpdatedCommitment`, { commitment: outcomeResponse.data, } );
	};

	const handleCreateUpdateUserCommitment = async ( { body, clientIdentifier: originatingClientIdentifier, } ) => {
		if ( originatingClientIdentifier === clientIdentifier ) {
			return debug( "ignoring message from self" );
		}

		const { outcomeId, teamId: outcomeTeamId, assignments = [], } = body;
		const { personId, } = store.getters[ "host-userProfile/userProfile" ];
		const teamId = store.getters[ `${ storeName }/teamId` ];

		// store is handling team outcomes, ignore this update
		if ( teamId || outcomeTeamId ) {
			return;
		}

		debug( "handling create/update commitment message for user" );

		// re-fetch the metadata regardless of whether assignment has changed, or
		// the commitment itself has changed
		const { data: metadata, } = await ioOutcome.fetchCommitmentMetadata();
		store.commit( `${ storeName }/receiveMetadata`, { metadata, } );

		// user has been removed as assignee, remove from the store if present
		if ( !assignments.includes( personId ) ) {
			return store.commit( `${ storeName }/removeDeletedCommitment`, { commitmentId: outcomeId, } );
		}

		// otherwise update as normal
		const { data: commitment, } = await ioOutcome.fetchOutcome( { outcomeId, } );
		store.commit( `${ storeName }/receiveUpdatedCommitment`, { commitment, } );
	};

	const handleDeleteTeamCommitment = async ( { body, clientIdentifier: originatingClientIdentifier, } ) => {
		if ( originatingClientIdentifier === clientIdentifier ) {
			return debug( "ignoring message from self" );
		}

		const { outcomeId: commitmentId, teamId: outcomeTeamId, } = body;
		const teamId = store.getters[ `${ storeName }/teamId` ];

		// message is not for this team, ignore
		if ( !teamId || outcomeTeamId !== teamId ) {
			return;
		}

		debug( `handling delete commitment message for team ${ teamId }` );

		store.commit( `${ storeName }/removeDeletedCommitment`, { commitmentId, } );

		const { data: metadata, } = await ioOutcome.fetchCommitmentMetadata( { teamId, } );
		store.commit( `${ storeName }/receiveMetadata`, { metadata, } );
	};

	const handleDeleteUserCommitment = async ( { body, clientIdentifier: originatingClientIdentifier, } ) => {
		if ( originatingClientIdentifier === clientIdentifier ) {
			return debug( "ignoring message from self" );
		}

		const { outcomeId, teamId: outcomeTeamId, } = body;
		const teamId = store.getters[ `${ storeName }/teamId` ];

		// store is handling team outcomes, ignore this update
		if ( teamId || outcomeTeamId ) {
			return;
		}

		debug( "handling delete commitment message for user" );

		store.commit( `${ storeName }/removeDeletedCommitment`, { commitmentId: outcomeId, } );

		const { data: metadata, } = await ioOutcome.fetchCommitmentMetadata();
		store.commit( `${ storeName }/receiveMetadata`, { metadata, } );
	};

	[
		SocketMessageType.OUTCOME_CREATED,
		SocketMessageType.OUTCOME_UPDATED,
	].forEach( socketEvent => {
		debug( `adding socket listener: ${ storeName } <= ${ socketEvent }` );
		ioSocket.addSocketMessageListener( socketEvent, handleCreateUpdateTeamCommitment );
		ioSocket.addSocketMessageListener( socketEvent, handleCreateUpdateUserCommitment );
	} );

	[
		SocketMessageType.OUTCOME_DELETED,
	].forEach( socketEvent => {
		debug( `adding socket listener: ${ storeName } <= ${ socketEvent }` );
		ioSocket.addSocketMessageListener( socketEvent, handleDeleteTeamCommitment );
		ioSocket.addSocketMessageListener( socketEvent, handleDeleteUserCommitment );
	} );
}

export function configureChannels( {
	storeName,
	store,
	ioChannel,
} ) {
	const debug = debugFactory( `teaming:common-team:stores:${ storeName }:channels` );
}
